import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "../lib/api";
import { CheckCircle, AlertCircle, Eye, EyeOff } from "lucide-react";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    if (!token) setError("Missing reset token. Request a new reset link.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (password !== confirm) { setError("Passwords do not match."); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/api/auth/reset-password-confirm`,
        { token, new_password: password },
        { withCredentials: true }
      );
      setSuccess(data?.message || "Password updated. You can now sign in.");
      setTimeout(() => navigate("/auth", { replace: true }), 2500);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-4" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-9 h-9 flex items-center justify-center">
            <img src="/synq-mark.png" alt="SYNQ" className="w-9 h-9 object-contain" draggable={false} />
          </div>
          <div className="leading-tight">
            <div className="font-outfit text-slate-900 text-lg font-bold">SYNQ — Project IQ</div>
            <div className="font-ibm-plex text-xs text-slate-400">Reset your password</div>
          </div>
        </div>

        <h1 className="font-outfit text-3xl font-black text-slate-900">Set a new password</h1>
        <p className="font-ibm-plex text-sm text-slate-500 mt-1 mb-6">
          Enter a new password for your account. Minimum 6 characters.
        </p>

        {success ? (
          <div data-testid="reset-success" className="border border-emerald-200 bg-emerald-50 rounded-lg p-4 flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-outfit font-semibold text-emerald-900">{success}</div>
              <Link to="/auth" className="font-ibm-plex text-sm text-emerald-700 underline mt-1 inline-block">Go to Sign In →</Link>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="reset-password-form">
            <div>
              <label className="block text-xs font-ibm-plex font-semibold text-slate-600 uppercase tracking-widest mb-1.5">
                New Password
              </label>
              <div className="relative">
                <input
                  data-testid="reset-password-input"
                  type={show ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 pr-10 text-sm font-ibm-plex focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                  placeholder="Min. 6 characters"
                  required
                />
                <button type="button" onClick={() => setShow(!show)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-xs font-ibm-plex font-semibold text-slate-600 uppercase tracking-widest mb-1.5">
                Confirm New Password
              </label>
              <input
                data-testid="reset-password-confirm-input"
                type={show ? "text" : "password"}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm font-ibm-plex focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                placeholder="Re-enter your new password"
                required
              />
            </div>
            {error && (
              <div data-testid="reset-error" className="border border-red-200 bg-red-50 rounded-lg p-3 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <span className="font-ibm-plex text-sm text-red-700">{error}</span>
              </div>
            )}
            <button
              data-testid="reset-submit-button"
              type="submit"
              disabled={loading || !token}
              className="w-full bg-slate-900 text-white font-outfit font-semibold py-2.5 rounded-lg hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? "Updating..." : "Update Password"}
            </button>
            <div className="text-center">
              <Link to="/auth" className="font-ibm-plex text-sm text-slate-500 hover:text-slate-700">
                Back to Sign In
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
